import useDeviceDetect from 'Hooks/useDeviceDetect';
import { useAvailability } from 'Modules/MultiStopWizard/Hooks/useAvailability';
import { useAvailabilityStatus } from 'Modules/MultiStopWizard/Hooks/useAvailabilityStatus';
import { useIsLegSelectable } from 'Modules/MultiStopWizard/Hooks/useIsLegSelectable';
import { useIsSoldOut } from 'Modules/MultiStopWizard/Hooks/useIsSoldOut';
import { useIsLegVisuallyDisabled } from 'Modules/MultiStopWizard/Hooks/useIsLegVisuallyDisabled';
import { useLegErrorMessage } from 'Modules/MultiStopWizard/Hooks/useLegErrorMessage';
import { useLegOptionClick } from 'Modules/MultiStopWizard/Hooks/useLegOptionClick';
import { useStopsBetween } from 'Modules/MultiStopWizard/Hooks/useStopsBetween';
import { TicketsTabTrip } from 'Services/Api/_HumanWritten/BookingWizardDataService';
import { useBookingWizardDataStore } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardDataStore';
import { LegOptionContextProps } from './LegOptionContext';

export interface UseLegOptionContextProps {
	trip: TicketsTabTrip;
	price?: number;
	isSelected?: boolean;
	isLoading?: boolean;
	legType: 'departure' | 'return';
	refreshTrips?: () => void;
}

/**
 * Everything a sailing card needs to know about its sailing, in one place.
 *
 * <p>Lifted out of <c>LegOption</c> so the redesigned cards can be drawn from the same facts rather
 * than from a second reading of them. Selectability, the reason a sailing cannot be taken, what is
 * left on it and what happens when it is pressed are decisions this wizard has already made, in
 * seven hooks, and a new card that re-derived any of them would be a new card that disagrees with
 * the old one about which sailings can be booked.</p>
 */
export function useBuildLegOptionContext({
	trip,
	price,
	isSelected = false,
	isLoading,
	legType,
	refreshTrips,
}: UseLegOptionContextProps): LegOptionContextProps {
	const { wizardData, setWizardData } = useBookingWizardDataStore();

	const { count: numberOfStopsBetween } = useStopsBetween(trip.startStopId, trip.endStopId);

	const availability = useAvailability(trip);
	const availabilityStatus = useAvailabilityStatus(trip, wizardData.tabSelected);
	// The same question `useLegErrorMessage` asks before it prints `SOLD OUT`, asked once here so a
	// card deciding what to draw and the row saying why cannot come to different answers.
	const soldOut = useIsSoldOut(wizardData.tabSelected, availability.passenger, availability.vehicle);

	const isSelectable = useIsLegSelectable(trip, wizardData, legType);
	const isVisuallyDisabled = useIsLegVisuallyDisabled(trip, wizardData);
	const onClick = useLegOptionClick(legType, wizardData, trip, true, setWizardData);

	const errorMessage = useLegErrorMessage(wizardData, trip, legType);

	return {
		// Legacy approach
		wizardData,
		trip,

		// Loading
		isLoading,

		// Booking details
		legType,

		// Selection details
		isSelected,
		isSelectable,
		isVisuallyDisabled,

		// Route details
		departureTime: trip.departureDateTime,
		arrivalTime: trip.arrivalDateTime,
		numberOfStopsBetween,

		// Price details
		price,
		hidePrice: wizardData.tabSelected === 'freight',

		// Space details
		availabilityStatus,
		soldOut,
		paxSpaces: availability.passenger,
		cargoSpaces: availability.vehicle,
		cargoSpacesExact: availability.vehicleExact,
		cargoSpacesBasedOnType: availability.measurementType === 'WEIGHT' ? 'weight' : 'length',

		capacityTags: trip.capacityTags,

		// Message
		errorMessage,

		// Waitlist
		isWaitlisted: trip.isWaitListed,
		waitlistFull: trip.waitlistFull,
		refreshTrips,

		// Closed
		vehicleBookingsClosed: trip.vehicleBookingsClosed ?? false,
		passengerBookingsClosed: trip.passengerBookingsClosed ?? false,
		freightBookingsClosed: trip.freightBookingsClosed ?? false,

		// Actions
		onClick,
	};
}

/** Whether this device gets the stacked phone card rather than the wide one. */
export function useIsNarrowTripCard(): boolean {
	const { isIpad } = useDeviceDetect();
	return isIpad;
}
